import { formatCurrency } from './formatting';

/**
 * Calculate product sales data from stock and sales
 */
export const calculateProductSales = (stock, salesDocs) => {
  if (!stock || !Array.isArray(salesDocs)) return { grid: [], byProduct: {} };

  const salesByProduct = {};

  salesDocs.forEach((docSnap) => {
    const sale = docSnap.data();
    if (sale.status !== 1) return;
    if (!sale.details) return;

    Object.keys(sale.details).forEach((productCategory) => {
      const items = Object.values(sale.details[productCategory] || {});
      items.forEach((product) => {
        const productKey = product?.label?.key;
        const stockProduct = stock?.stock?.[productCategory]?.find(r => r.id === productKey);
        if (!productKey || !stockProduct) return;

        const unitPrice = stockProduct.price;
        const computedPrice = unitPrice * product.count;

        if (!salesByProduct[productKey]) {
          salesByProduct[productKey] = {
            sold: 0,
            totalSale: 0,
            price: unitPrice,
            stock: stockProduct.stock,
            title: product?.label?.title?.en,
            category: productCategory,
            hasDownPayment: !!stockProduct.hasDownPayment,
            downPaymentAmount: stockProduct.downPaymentAmount ? stockProduct.downPaymentAmount : 0,
          };
        }

        salesByProduct[productKey].sold += product.count;
        salesByProduct[productKey].totalSale += computedPrice;
      });
    });
  });

  const grid = Object.keys(salesByProduct).map((productID) => ({
    id: productID,
    title: salesByProduct[productID].title,
    category: salesByProduct[productID].category,
    price: salesByProduct[productID].hasDownPayment ? salesByProduct[productID].downPaymentAmount : salesByProduct[productID].price,
    stock: salesByProduct[productID].stock,
    sold: salesByProduct[productID].sold,
    totalSale: salesByProduct[productID].totalSale,
    remaining: salesByProduct[productID].stock - salesByProduct[productID].sold,
  }));

  let totalSold = 0;
  let totalSale = 0;
  grid.forEach((p) => {
    totalSold += p.sold;
    totalSale += (p.totalSale ? p.totalSale : (p.sold * p.price));
  });
  grid.push({ id: 'total', category: 'Total', title: '', price: '', stock: '', sold: totalSold, totalSale });

  return { grid, byProduct: salesByProduct };
};

/**
 * Calculate remaining payments data
 */
export const calculateRemainingPayments = (stock, salesDocs) => {
  if (!stock || !Array.isArray(salesDocs)) return [];

  const rows = [];

  salesDocs.forEach((docSnap) => {
    const sale = docSnap.data();
    if (!sale.details) return;
    Object.keys(sale.details).forEach((productCategory) => {
      const items = Object.values(sale.details[productCategory] || {});
      items.forEach((product) => {
        const productKey = product?.label?.key;
        const stockProduct = stock?.stock?.[productCategory]?.find(r => r.id === productKey);
        if (!productKey || !stockProduct || !stockProduct.hasDownPayment) return;
        rows.push({
          id: productKey,
          category: productCategory,
          title: product?.label?.title?.en,
          price: stockProduct.price,
          stock: stockProduct.stock,
          sold: product.count,
          downPaymentAmount: stockProduct.downPaymentAmount,
        });
      });
    });
  });

  let totalSold = 0;
  let totalRemainingAmount = 0;
  rows.forEach((p) => {
    totalSold += p.sold;
    const remainingAmount = p.price - p.downPaymentAmount;
    totalRemainingAmount += p.sold * remainingAmount;
  });
  rows.push({ id: 'total', category: 'Total', title: '', price: '', stock: '', sold: totalSold, totalRemainingAmount });

  return rows;
};

/**
 * Calculate coupons data
 */
export const calculateCouponsData = (event) => {
  if (!event?.coupons) return [];
  
  const couponsData = [];
  event.coupons.forEach((coupon) => {
    couponsData.push({
      id: coupon.code.toLowerCase().concat(Math.random().toString(36).substring(7)),
      code: coupon.code,
      discount: coupon.discount,
      stock: coupon.stock,
      used: coupon.used,
    });
  });

  let totalDiscount = 0;
  let totalUsed = 0;
  couponsData.forEach((c) => {
    if (c.used) {
      totalDiscount += (c.discount * c.used);
      totalUsed += c.used ? c.used : 0;
    }
  });
  couponsData.push({ id: 'total', code: 'Total', stock: '', used: '', remaining: '', discount: '', totalDiscount, totalUsed });
  
  return couponsData;
};

/**
 * Calculate commission and totals
 */
export const calculateCommission = (event, totalStockSale, totalActualSale, totalOrganisatorCashSales, coupons, remaining) => {
  const sub_total = totalStockSale - coupons - remaining - totalOrganisatorCashSales;
  const commission = sub_total * (event.commissionFee / 100);
  
  const checkCalc = sub_total + totalOrganisatorCashSales === totalActualSale - (event.isCommissionExtra ? commission : 0);
  
  return {
    subTotal: sub_total,
    commission,
    checkCalc
  };
};

/**
 * Calculate actual sales data from sales documents
 */
export const calculateActualSalesData = (salesDocs) => {
  let totalSaleAmount = 0;
  let totalSuccessfullSalesCount = 0;
  let totalAmountOfCashSales = 0;
  let totalAmountOfCCSales = 0;
  let totalSoldTickets = 0;

  salesDocs.forEach((doc) => {
    const data = doc.data();
    totalSaleAmount += data.paymentTotal;
    totalSuccessfullSalesCount++;

    if (data.tickets && data.tickets.length && parseInt(data.tickets.length)) {
      totalSoldTickets += parseInt(data.tickets.length);
    }

    if (data.paymentMethod === 'cash') {
      totalAmountOfCashSales += data.paymentTotal;
    } else {
      totalAmountOfCCSales += data.paymentTotal;
    }
  });

  return {
    totalSaleAmount,
    totalSuccessfullSalesCount,
    totalAmountOfCashSales,
    totalAmountOfCCSales,
    totalSoldTickets
  };
};
